// Runtime config from window._env_ (Docker) with fallback to build-time env
// vars (.env-cmdrc.json via env-cmd). This lets a single built image be run
// against any backend/host by passing -e vars to `docker run` — no rebuild
// needed. See docker-entrypoint.sh (generates window._env_ at container
// startup) and DOCKER_ENV_CONFIG.md for the full picture.
declare global {
  interface Window {
    _env_?: {
      REACT_APP_LIVIS_API_URL?: string;
      REACT_APP_IS_SSE?: string;
      REACT_APP_UPLOAD_API_URL?: string;
      REACT_APP_DEPLOYMENT?: string;
      REACT_APP_AI_AGENT_URL?: string;
      REACT_APP_MAINTENANCE_START_UTC?: string;
      REACT_APP_MAINTENANCE_END_UTC?: string;
      REACT_APP_RELEASE_VERSION?: string;
      REACT_APP_RELEASE_DESCRIPTION?: string;
      REACT_APP_RELEASE_NOTES_URL?: string;
      REACT_APP_LOG_ENABLE?: string;
      REACT_APP_LOG_API_URL?: string;
      REACT_APP_LOG_FLUSH_INTERVAL_MS?: string;
      REACT_APP_LOG_MAX_BUFFER_SIZE?: string;
    };
  }
}

const getEnv = (key: string): string | undefined => {
  // First check runtime config (Docker), then fall back to build-time env.
  const runtimeValue = window._env_?.[key as keyof typeof window._env_];
  // Use runtime value only if it's a non-empty string.
  if (typeof runtimeValue === "string" && runtimeValue.length > 0) {
    return runtimeValue;
  }
  return process.env[key];
};

export const LIVIS_API_BASE_URL = getEnv("REACT_APP_LIVIS_API_URL");
export const SSE_ENABLE = getEnv("REACT_APP_IS_SSE");
export const UPLOAD_URL = getEnv("REACT_APP_UPLOAD_API_URL");
export const DEPLOYMENT_TYPE = getEnv("REACT_APP_DEPLOYMENT");
export const AI_AGENT_BASE_URL = getEnv("REACT_APP_AI_AGENT_URL");

// Drives the sidebar "What's New" carousel slide only — update these instead
// of touching Sidebar.tsx when a new release goes out.
export const RELEASE_VERSION = getEnv("REACT_APP_RELEASE_VERSION");
export const RELEASE_DESCRIPTION = getEnv("REACT_APP_RELEASE_DESCRIPTION");
export const RELEASE_NOTES_URL = getEnv("REACT_APP_RELEASE_NOTES_URL");

export const MAINTENANCE_START_UTC = getEnv("REACT_APP_MAINTENANCE_START_UTC");
export const MAINTENANCE_END_UTC = getEnv("REACT_APP_MAINTENANCE_END_UTC");

// End-to-end frontend logging (see src/services/logger). The only supported
// sink is the Django log server, deployed and run entirely on its own —
// this repo never starts it. Both whether logging is on and where it's sent
// are env-controlled per environment, no rebuild needed.
//
// Disabled unless explicitly turned on: only the literal string "Yes"
// enables logging. Every environment in .env-cmdrc.json sets this
// explicitly to "No" until an environment actually wants it on.
export const LOG_ENABLE = getEnv("REACT_APP_LOG_ENABLE") || "No";
// The Django log server's URL for this environment (see endpoint.ts). If
// unset, logging silently no-ops even when LOG_ENABLE is "Yes" — there is
// no fallback destination.
export const LOG_API_URL = getEnv("REACT_APP_LOG_API_URL");

// Client-side batching (see src/services/logger/logger.ts) — how often the
// buffer is flushed, and how many events force an early flush. Raise these
// to reduce request frequency on pages with heavy activity (many API calls/
// clicks per second can otherwise hit MAX_BUFFER_SIZE well before the timer
// fires). Same defaults as before this became configurable.
export const LOG_FLUSH_INTERVAL_MS = parseInt(
  getEnv("REACT_APP_LOG_FLUSH_INTERVAL_MS") || "5000",
  10
);
export const LOG_MAX_BUFFER_SIZE = parseInt(
  getEnv("REACT_APP_LOG_MAX_BUFFER_SIZE") || "20",
  10
);

//If you are adding a new variable make sure to add REACT_APP prefix , also make sure to export from index.ts - config/index.ts
//For Docker runtime config, also update public/env-config.js and docker-entrypoint.sh
